import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  engineeringExcellenceApi,
  type ArchitectureComplianceSnapshot,
  type CodeQualitySnapshot,
  type DeveloperExperienceSnapshot,
  type ExcellenceDashboardSnapshot,
  type ImprovementCycle,
  type ObservabilitySnapshot,
  type PerformanceBudgetEntry,
  type ReliabilitySnapshot,
  type ReleaseEngineeringSnapshot,
  type TechnicalDebtItem,
  type TestingStandardsSnapshot,
} from '../../api/engineering-excellence-exl001-runtime';

type Tab = 'dashboard' | 'code_quality' | 'architecture' | 'tech_debt' | 'reliability' | 'performance' | 'devex' | 'observability' | 'testing' | 'release' | 'improvement';

export function EngineeringExcellencePage() {
  const [tab, setTab] = useState<Tab>('dashboard');
  const [dashboard, setDashboard] = useState<ExcellenceDashboardSnapshot | null>(null);
  const [codeQuality, setCodeQuality] = useState<CodeQualitySnapshot | null>(null);
  const [architecture, setArchitecture] = useState<ArchitectureComplianceSnapshot | null>(null);
  const [techDebt, setTechDebt] = useState<TechnicalDebtItem[]>([]);
  const [reliability, setReliability] = useState<ReliabilitySnapshot | null>(null);
  const [performance, setPerformance] = useState<PerformanceBudgetEntry[]>([]);
  const [devex, setDevex] = useState<DeveloperExperienceSnapshot | null>(null);
  const [observability, setObservability] = useState<ObservabilitySnapshot | null>(null);
  const [testing, setTesting] = useState<TestingStandardsSnapshot | null>(null);
  const [release, setRelease] = useState<ReleaseEngineeringSnapshot | null>(null);
  const [improvement, setImprovement] = useState<ImprovementCycle[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const card = 'border-amber-200 bg-white dark:border-amber-900/40 dark:bg-zinc-900';
  const muted = 'text-slate-500 dark:text-zinc-400';
  const accent = 'text-amber-700 dark:text-amber-300';
  const shell = 'bg-amber-50/20 dark:bg-zinc-950';

  const refresh = useCallback(async () => {
    setError(null); setLoading(true);
    try {
      const [dash, cq, arch, debt, rel, perf, dx, obs, test, relEng, imp] = await Promise.all([
        engineeringExcellenceApi.dashboard(), engineeringExcellenceApi.codeQuality(),
        engineeringExcellenceApi.architecture(), engineeringExcellenceApi.techDebt(),
        engineeringExcellenceApi.reliability(), engineeringExcellenceApi.performance(),
        engineeringExcellenceApi.devex(), engineeringExcellenceApi.observability(),
        engineeringExcellenceApi.testing(), engineeringExcellenceApi.release(),
        engineeringExcellenceApi.improvement(),
      ]);
      setDashboard(dash); setCodeQuality(cq); setArchitecture(arch); setTechDebt(debt);
      setReliability(rel); setPerformance(perf); setDevex(dx); setObservability(obs);
      setTesting(test); setRelease(relEng); setImprovement(imp);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load Engineering Excellence');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const tabs: { id: Tab; label: string }[] = [
    { id: 'dashboard', label: 'Overview' }, { id: 'code_quality', label: 'Code Quality' },
    { id: 'architecture', label: 'Architecture' }, { id: 'tech_debt', label: 'Tech Debt' },
    { id: 'reliability', label: 'Reliability' }, { id: 'performance', label: 'Performance' },
    { id: 'devex', label: 'DevEx' }, { id: 'observability', label: 'Observability' },
    { id: 'testing', label: 'Testing' }, { id: 'release', label: 'Release' }, { id: 'improvement', label: 'Improvement' },
  ];

  return (
    <div className={`space-y-4 rounded-xl p-1 ${shell}`} role="main">
      <header className={`rounded-xl border p-4 ${card}`}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>EXL-000001 · Engineering Excellence & World-Class Standards</p>
            <h1 className="text-xl font-semibold">Engineering Excellence Command Center</h1>
            {dashboard && (
              <div className={`mt-2 flex flex-wrap gap-x-4 text-sm ${muted}`}>
                <span>Excellence <strong className={accent}>{dashboard.excellence_score}</strong></span>
                <span>Coverage <strong className={accent}>{dashboard.test_coverage_pct}%</strong></span>
                <span>Debt Items <strong className={accent}>{dashboard.open_debt_items}</strong></span>
                <span>Violations <strong className={accent}>{dashboard.critical_violations}</strong></span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Link to="/ai-governance" className={`rounded-lg border px-3 py-1.5 text-sm ${card} ${accent}`}>AI Governance</Link>
            <Link to="/platform/engineering-excellence-exl001" className={`rounded-lg border px-3 py-1.5 text-sm ${card} ${accent}`}>Sprint Center</Link>
          </div>
        </div>
      </header>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {loading && <p className={`text-sm ${muted}`}>Loading engineering excellence…</p>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? 'bg-amber-600 text-white' : `${card} border`}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'dashboard' && dashboard && (
        <div className="grid gap-3 sm:grid-cols-4">
          {[
            { label: 'Excellence Score', value: dashboard.excellence_score },
            { label: 'Code Quality', value: dashboard.code_quality_score },
            { label: 'Architecture', value: dashboard.architecture_score },
            { label: 'Reliability', value: dashboard.reliability_score },
          ].map((m) => (
            <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
              <p className="text-2xl font-semibold">{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'code_quality' && codeQuality && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <p className={`text-lg font-semibold ${codeQuality.static_analysis_pass ? 'text-amber-600' : 'text-red-600'}`}>
            Static Analysis: {codeQuality.static_analysis_pass ? 'Pass' : 'Fail'}
          </p>
          <div className="mt-2 grid gap-1 text-sm sm:grid-cols-2">
            <p>Lint violations: {codeQuality.lint_violations}</p>
            <p>Complexity breaches: {codeQuality.complexity_breaches}</p>
            <p>Security findings: {codeQuality.security_findings}</p>
            <p>Format compliance: {codeQuality.format_compliance_pct}%</p>
          </div>
        </div>
      )}

      {tab === 'architecture' && architecture && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <p className="font-semibold">ADR Compliance: {architecture.adr_compliance_pct}%</p>
          <p className={`text-sm ${muted}`}>Repo structure: {architecture.repo_structure_valid ? 'Valid' : 'Invalid'} · Layer isolation: {architecture.layer_isolation_ok ? 'OK' : 'Violated'}</p>
          <p className={`text-sm ${muted}`}>Dependency violations: {architecture.dependency_violations} · Contract violations: {architecture.contract_violations}</p>
        </div>
      )}

      {tab === 'tech_debt' && techDebt.map((d) => (
        <div key={d.id} className={`rounded-xl border p-4 ${card}`}>
          <div className="flex justify-between"><p className="font-semibold">{d.title}</p><span className="text-xs uppercase">{d.priority}</span></div>
          <p className={`text-xs ${muted}`}>{d.owner} · {d.estimated_cost} · Risk: {d.business_risk} · {d.status}</p>
        </div>
      ))}

      {tab === 'reliability' && reliability && (
        <div className="grid gap-3 sm:grid-cols-3">
          {[
            { label: 'Availability', value: `${reliability.availability_pct}%` },
            { label: 'Error Rate', value: `${reliability.error_rate_pct}%` },
            { label: 'MTTR', value: `${reliability.mttr_minutes}m` },
            { label: 'MTTD', value: `${reliability.mttd_minutes}m` },
            { label: 'Deploy Success', value: `${reliability.deployment_success_pct}%` },
            { label: 'Incidents (30d)', value: reliability.incident_count_30d },
          ].map((m) => (
            <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
              <p className="text-xl font-semibold">{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'performance' && performance.map((b) => (
        <div key={b.key} className={`rounded-xl border p-4 ${card}`}>
          <div className="flex justify-between"><p className="font-semibold">{b.key.replace(/_/g, ' ')}</p><span className={`text-xs uppercase ${b.status === 'pass' ? 'text-amber-600' : 'text-red-600'}`}>{b.status}</span></div>
          <p className={`text-sm ${accent}`}>Target {b.target} · Current {b.current}</p>
        </div>
      ))}

      {tab === 'devex' && devex && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <p className="font-semibold">Build {devex.build_time_seconds}s · Test {devex.test_time_seconds}s · Setup {devex.local_setup_minutes}m</p>
          <p className={`text-sm ${muted}`}>Hot reload: {devex.hot_reload_enabled ? 'Enabled' : 'Disabled'} · Doc coverage: {devex.doc_coverage_pct}%</p>
        </div>
      )}

      {tab === 'observability' && observability && (
        <div className="grid gap-3 sm:grid-cols-3">
          {[
            { label: 'Metrics', value: `${observability.metrics_coverage_pct}%` },
            { label: 'Tracing', value: `${observability.tracing_coverage_pct}%` },
            { label: 'Logging', value: `${observability.structured_logging_pct}%` },
            { label: 'SLO Compliance', value: `${observability.slo_compliance_pct}%` },
            { label: 'Active Alerts', value: observability.active_alerts },
          ].map((m) => (
            <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
              <p className="text-xl font-semibold">{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'testing' && testing && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <p className="font-semibold">Unit {testing.unit_coverage_pct}% · Integration {testing.integration_coverage_pct}%</p>
          <p className={`text-sm ${muted}`}>
            Contract {testing.contract_tests_pass ? '✓' : '✗'} · E2E {testing.e2e_tests_pass ? '✓' : '✗'} · Load {testing.load_tests_pass ? '✓' : '✗'} · Security {testing.security_tests_pass ? '✓' : '✗'} · A11y {testing.a11y_tests_pass ? '✓' : '✗'}
          </p>
        </div>
      )}

      {tab === 'release' && release && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <p className="font-semibold">{release.branch_strategy} · {release.release_cadence}</p>
          <p className={`text-sm ${muted}`}>Canary: {release.canary_enabled ? 'Enabled' : 'Disabled'} · Flags: {release.feature_flags_active} · Last: {release.last_release_status} · Rollback ready: {release.rollback_ready ? 'Yes' : 'No'}</p>
        </div>
      )}

      {tab === 'improvement' && improvement.map((c) => (
        <div key={c.id} className={`rounded-xl border p-4 ${card}`}>
          <div className="flex justify-between"><p className="font-semibold">{c.action}</p><span className="text-xs uppercase">{c.status}</span></div>
          <p className={`text-xs ${muted}`}>{c.area} · {c.impact}</p>
        </div>
      ))}
    </div>
  );
}
